import { AuditRow } from './auditApi';
import { Verdict } from './engine/types';
import {
  AuditRoleType,
  ROLES_BY_CATEGORY,
  VerdictClass,
  VerdictLabel
} from './campaign-audit-constants';

// ── Audit roles (who audits which rows) ──────────────────────────────────────────
// Role is derived from the row's category (see ROLES_BY_CATEGORY). rowMatchesRole with a null role
// means "all roles" — the unfiltered default for the Overview role filter.

export const rolesForCategory = (category: AuditRow['category']): AuditRoleType[] =>
  ROLES_BY_CATEGORY[category] ?? [];

export const rowMatchesRole = (
  row: Pick<AuditRow, 'category'>,
  role: AuditRoleType | null
): boolean => role === null || rolesForCategory(row.category).includes(role);

// ── Flag counts + health tier (replaces the 0–100 score, CA-1) ──────────────────
//
// The tool is a per-client work queue, not a grade. A scope (overall / category / theme) is summarized
// as counts of flags — how many rows need investigating — plus coverage, never a composite number.

export const HealthTier = {
  AT_RISK: 'at-risk',
  WATCH: 'watch',
  CLEAR: 'clear',
  // Nothing is flagged, but too little was answered to call the scope Clear.
  INSUFFICIENT: 'insufficient'
} as const;
export type HealthTierType = (typeof HealthTier)[keyof typeof HealthTier];

export const HealthTierLabel: Record<HealthTierType, string> = {
  [HealthTier.AT_RISK]: 'At-risk',
  [HealthTier.WATCH]: 'Watch',
  [HealthTier.CLEAR]: 'Clear',
  [HealthTier.INSUFFICIENT]: 'Insufficient coverage'
};

// A scope reads At-risk once this many rows are Investigation-required, even without a mission-critical
// one among them. Mission-critical Investigation-required flips it to At-risk on its own.
const AT_RISK_REQUIRED_THRESHOLD = 3;

// A scope with no flags still isn't "Clear" if too little was actually answered — below this
// answered-share (and when nothing is applicable) it reads "Insufficient coverage" so thin
// coverage can't masquerade as a healthy result. Tune as real usage informs it.
const MIN_COVERAGE_FOR_CLEAR = 0.5;

export interface RowSummary {
  required: number; // Investigation required (Alarming), applicable
  recommend: number; // Recommend investigation (Concerning), applicable
  clear: number; // Clear (Good), applicable
  unanswered: number; // applicable, no verdict yet
  mcRequired: number; // mission-critical AND investigation-required
  mcUnanswered: number; // mission-critical AND unanswered
  applicable: number; // not marked N/A
  answered: number; // applicable AND has a verdict
  coverage: number; // answered / applicable (0 when nothing applicable)
  // Confidence split of ANSWERED AUTO rows (manual answers carry no source tier). Lets the UI warn
  // that a chunk of the verdicts — including any "Clear" — rest on AI/heuristic signals, so a low-
  // confidence Clear isn't over-trusted like a measured one.
  lowConfidence: number; // answered auto rows whose signal is ai or heuristic
}

// A row that's out of the active work queue: not applicable, or a human-"accepted" known gap. Both are
// excluded from scoring, the counts, and the attention/priority lists — but still rendered in the
// category tab (accepted rows stay visible as an accepted gap).
export const isInactive = (r: Pick<AuditRow, 'notApplicable' | 'disposition'>): boolean =>
  Boolean(r.notApplicable) || r.disposition === 'accepted';

export const summarizeRows = (rows: AuditRow[]): RowSummary => {
  const applicableRows = rows.filter(r => !isInactive(r));
  const has = (v: Verdict) => (r: AuditRow) => r.verdict === v;
  const required = applicableRows.filter(has('Alarming'));
  const recommend = applicableRows.filter(has('Concerning'));
  const clear = applicableRows.filter(has('Good'));
  const answered = applicableRows.filter(r => r.verdict !== undefined);
  const unanswered = applicableRows.filter(r => r.verdict === undefined);

  return {
    required: required.length,
    recommend: recommend.length,
    clear: clear.length,
    unanswered: unanswered.length,
    mcRequired: required.filter(r => r.missionCritical).length,
    mcUnanswered: unanswered.filter(r => r.missionCritical).length,
    applicable: applicableRows.length,
    answered: answered.length,
    coverage: applicableRows.length === 0 ? 0 : answered.length / applicableRows.length,
    lowConfidence: answered.filter(
      r => r.sourceConfidence === 'ai' || r.sourceConfidence === 'heuristic'
    ).length
  };
};

export const healthTier = (s: RowSummary): HealthTierType => {
  if (s.mcRequired > 0 || s.required >= AT_RISK_REQUIRED_THRESHOLD) return HealthTier.AT_RISK;
  if (s.required > 0 || s.recommend > 0 || s.mcUnanswered > 0) return HealthTier.WATCH;
  // Nothing flagged — but don't call it Clear if nothing (or too little) was actually answered.
  if (s.applicable === 0 || s.coverage < MIN_COVERAGE_FOR_CLEAR) return HealthTier.INSUFFICIENT;
  return HealthTier.CLEAR;
};

// ── Load resilience (reliability) ────────────────────────────────────────────────
// Alpha testers hit two distinct load failures that looked the same on screen: (1) a transient failure
// — the async audit run hadn't been delivered yet (cold start / token refresh), so the first load threw
// and the user had to click "retry" a few times before it worked; and (2) a permanent one — the client
// isn't onboarded to the tool, which no amount of retrying fixes and should point the user at "Add
// client" rather than a red error. These helpers separate the two.

// The backend throws "No client config found for '<cid>' — onboard this client first" for an
// un-onboarded client. That's an actionable setup state, not a transient error to retry or a failure to
// surface in red. Matched by message so the page can route it to an onboarding CTA.
export const isNoClientConfigError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return /no client config|onboard this client/i.test(message);
};

export interface RetryOptions {
  attempts: number; // total tries, including the first
  delayMs: number; // base backoff; grows linearly (delayMs, 2×delayMs, …)
  // Return true for a failure that a retry can't fix (e.g. no client config) — rethrow immediately.
  shouldStop?: (error: unknown) => boolean;
  // Injectable so unit tests don't wait on real timers.
  sleep?: (ms: number) => Promise<void>;
}

// Retry an async load on transient failure with linear backoff. Rethrows the last error once attempts
// are exhausted or shouldStop matches — so a permanent error surfaces immediately, not after N waits.
export const retryAsync = async <T>(
  fn: () => Promise<T>,
  {
    attempts,
    delayMs,
    shouldStop,
    sleep = ms =>
      new Promise<void>(resolve => {
        setTimeout(resolve, ms);
      })
  }: RetryOptions
): Promise<T> => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fn();
    } catch (e) {
      if (attempt >= attempts || shouldStop?.(e)) throw e;
      await sleep(delayMs * attempt);
    }
  }
};

// ── Source-confidence badge (WS-3) ──────────────────────────────────────────────
// Keyed off AuditRow['sourceConfidence'] ('ai' | 'heuristic' | 'measured').

type SourceConfidence = NonNullable<AuditRow['sourceConfidence']>;

export const SourceConfidenceLabel: Record<SourceConfidence, string> = {
  ai: 'AI-derived',
  heuristic: 'Heuristic',
  measured: 'Measured'
};

export const SourceConfidenceTitle: Record<SourceConfidence, string> = {
  ai: 'AI-derived (Claude Haiku 4.5 + web search) — non-deterministic; confirm before acting.',
  heuristic:
    'Heuristic — real data classified by a name/regex proxy or a stand-in metric; medium confidence.',
  measured: 'Measured — a direct read of a real data field; highest confidence.'
};

export const SourceConfidenceClass: Record<SourceConfidence, string> = {
  ai: 'ai',
  heuristic: 'heuristic',
  measured: 'measured'
};

// ── Manual-row verdict guidance (WS-3 popup) ─────────────────────────────────────
// Manual / degraded rows carry their guidance in `expectation`, composed as
// "Good: … · Concerning: … · Alarming: …" (a degraded row may lead with a plain expectation
// sentence). Parse it back into per-verdict lines so the popup can render each with the reframed
// verdict labels (Clear / Recommend investigation / Investigation required).

export interface ParsedGuidance {
  intro: string[];
  items: { verdict: Verdict; label: string; text: string }[];
}

// ── Row state flag (folded row) ──────────────────────────────────────────────

// One small colour-coded flag summarises a row's state on the folded row, so the list reads as
// question + state + location and nothing else (Harry 2026-09-09). Six states, and the order below
// IS the precedence:
//   N/A                     deliberately excluded from scoring — wins even if a verdict was derived
//   Not enough information  a deriver refused for lack of data; NOT the same as nobody answering
//   Clear / Recommend investigation / Investigation required
//   Unanswered              no verdict, and nothing stopping one
export const rowFlag = (
  row: Pick<AuditRow, 'notApplicable' | 'insufficientData' | 'verdict'>
): { label: string; className: string } => {
  if (row.notApplicable) return { label: 'N/A', className: 'na' };
  if (row.insufficientData) return { label: 'Not enough information', className: 'no-data' };
  if (row.verdict)
    return { label: VerdictLabel[row.verdict], className: VerdictClass[row.verdict] };
  return { label: 'Unanswered', className: 'unanswered' };
};

const VERDICT_PREFIX = /^(Good|Concerning|Alarming):\s*(.*)$/;

export const parseManualGuidance = (expectation?: string): ParsedGuidance => {
  const result: ParsedGuidance = { intro: [], items: [] };
  if (!expectation) return result;
  for (const segment of expectation.split(' · ')) {
    const match = segment.match(VERDICT_PREFIX);
    if (match) {
      const verdict = match[1] as Verdict;
      result.items.push({ verdict, label: VerdictLabel[verdict], text: match[2] });
    } else if (segment.trim()) {
      result.intro.push(segment.trim());
    }
  }
  return result;
};

// ── Findings export (CA-7, internal-only) ────────────────────────────────────────
// Serialize the current audit's rows to CSV so a lead can pull findings into their own QBR prep
// without copy-paste. Verdicts use the reframed labels. Internal-use only — no client-share affordance.

// First row of every export: an unmissable internal-only warning so the file carries the guardrail
// the on-screen disclaimer has, even after it leaves the tool.
const INTERNAL_BANNER =
  'INTERNAL USE ONLY — DO NOT SHARE WITH CLIENTS. Heuristic/AI-derived; confirm before relying.';

// Re-label a manual/degraded row's raw `expectation` (composed with the internal
// "Good/Concerning/Alarming:" prefixes) using the reframed verdict labels, so an exported file never
// carries the internal taxonomy. Mirrors what parseManualGuidance renders on screen.
const relabelGuidance = (expectation?: string): string => {
  const { intro, items } = parseManualGuidance(expectation);
  return [...intro, ...items.map(i => `${i.label}: ${i.text}`)].join(' · ');
};

const CSV_COLUMNS: { header: string; value: (r: AuditRow) => string }[] = [
  { header: 'ID', value: r => String(r.id) },
  { header: 'Category', value: r => r.category },
  { header: 'Topic', value: r => r.topic },
  { header: 'Question', value: r => r.question },
  { header: 'Verdict', value: r => (r.verdict ? VerdictLabel[r.verdict] : '') },
  { header: 'Mission-critical', value: r => (r.missionCritical ? 'yes' : '') },
  { header: 'N/A', value: r => (r.notApplicable ? 'yes' : '') },
  { header: 'Origin', value: r => r.origin },
  { header: 'Source confidence', value: r => r.sourceConfidence ?? '' },
  { header: 'Observed', value: r => r.observedSummary ?? '' },
  { header: 'Rationale / note', value: r => r.rationale ?? '' },
  { header: 'Guidance', value: r => relabelGuidance(r.expectation) },
  { header: 'Degraded reason', value: r => r.degradedReason ?? '' }
];

// A cell whose text begins with = + - @ (or a tab/CR that can shift the parser onto one of them)
// is interpreted as a formula by Excel/Sheets on open — a CSV-injection vector even for an
// internal-only export. Prefix a single quote so the value is always treated as literal text.
const neutralizeFormula = (value: string): string =>
  /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

// RFC-4180-ish quoting: wrap in quotes and double any embedded quotes; keeps commas/newlines safe.
const csvCell = (value: string): string => `"${neutralizeFormula(value).replace(/"/g, '""')}"`;

export const auditRowsToCsv = (rows: AuditRow[]): string =>
  [
    csvCell(INTERNAL_BANNER),
    CSV_COLUMNS.map(c => csvCell(c.header)).join(','),
    ...rows.map(row => CSV_COLUMNS.map(c => csvCell(c.value(row))).join(','))
  ].join('\r\n');

export const downloadCsv = (filename: string, csv: string): void => {
  // Prepend a UTF-8 BOM (U+FEFF) so Excel opens accented characters correctly.
  const bom = String.fromCharCode(0xfeff);
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
